import React from 'react'
import { Box, Stack, Heading, Text, Switch, Select, Card, FormControl, Alert } from '@/components/lib'
import api from '../../../api'
import utils from '@/utils/utils'
import useStore from '../../../store'

// Available food-diary backends. MyFood24 is the only source for now, but the
// data model (trial.foodDiaryDataSource) is a plain enum so more can be added.
const DATA_SOURCES = [
  { value: 'myfood24', label: 'MyFood24' }
]

const DEFAULT_DATA_SOURCE = 'myfood24'

export default function FoodDiaries () {
  const { trial, updateTrial, team } = useStore()

  const hasFoodDiaries = utils.hasFeature(team, 'foodDiaries')
  const enabled = !!trial?.foodDiariesEnabled
  const dataSource = trial?.foodDiaryDataSource || DEFAULT_DATA_SOURCE

  function update (upd) {
    api.trials.update(trial._id, upd, t => updateTrial(t._id, t), err => utils.toast('error', err.message))
  }

  function toggleEnabled (e) {
    // On first enable, also persist the (defaulted) data source so it is stored
    // explicitly rather than only reflected in the UI.
    update(
      e.checked && !trial?.foodDiaryDataSource
        ? { foodDiariesEnabled: true, foodDiaryDataSource: dataSource }
        : { foodDiariesEnabled: e.checked }
    )
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading size='md'>Food diaries</Heading>
        <Text>
          When enabled, a participant's food diary records what they have eaten and drunk. Foods are
          looked up from the chosen data source, providing nutritional information and portion sizes.
        </Text>
      </Box>

      <Alert status='info'>
        Food diaries are currently a coordinator-only (CRF) feature — entries are added from a
        participant's record in the web app. They are not yet available to participants via the mobile app.
      </Alert>

      <Card variant='outline' gap={4}>
        {hasFoodDiaries
          ? (
            <>
              <Stack alignItems='center' direction='row' spacing={4}>
                <Switch id='foodDiaries' checked={enabled} onChange={toggleEnabled} />
                <Text>Enable food diaries for this trial</Text>
              </Stack>

              <FormControl label='Data source'>
                <Select
                  maxW='300px'
                  value={dataSource}
                  disabled={!enabled}
                  onChange={e => update({ foodDiaryDataSource: e.target.value })}
                >
                  {DATA_SOURCES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Select>
              </FormControl>
            </>)
          : (
            <Alert status='info'>
              Food diaries are not enabled for this team. Contact your account manager for more information.
            </Alert>)}
      </Card>
    </Stack>
  )
}
